import { memo, useMemo } from 'react';

import { toShareTarget } from '@/features/preset-sharing';
import canEditPresetInputs from '@/utils/preset/canEditPresetInputs';

import FeedActionBar from '../FeedActionBar';
import FeedSlideMedia from '../FeedSlideMedia';
import useStyles from './styles';

import type { FeedSlideListProps } from './types';

const VIDEO_PRELOAD_WINDOW = 1;
const POSTER_PRELOAD_WINDOW = 3;
const MEDIA_MOUNT_WINDOW = 5;
/**
 * Slides that may quote their credit cost. Each quote is a request, and 11
 * slides are mounted at once — the neighbours are included so a swipe shows the
 * count immediately instead of a spinner, and nothing further out asks at all.
 */
const CREDITS_QUOTE_WINDOW = 1;

const FeedSlideList = ({
  items,
  activeIndex,
  scrollRef,
  setSlideRef,
  isMuted,
  onAudioDetected,
  onUploadTap,
  onEditTap,
  isUploadLoading,
  shouldLoadCredits,
}: FeedSlideListProps) => {
  const classes = useStyles();
  // Memoized over the whole list so each bar receives a stable object and its
  // `memo` isn't defeated by a fresh literal on every render.
  const shareTargets = useMemo(() => items.map(toShareTarget), [items]);

  return (
    <div ref={scrollRef} className={classes.scrollArea}>
      {items.map((item, index) => {
        const { slug, media, type, flowId } = item;
        const distance = Math.abs(index - activeIndex);
        return (
          <div
            key={slug}
            data-index={index}
            data-testid={slug}
            data-active={index === activeIndex || undefined}
            ref={setSlideRef(index)}
            className={classes.slide}
          >
            {distance <= MEDIA_MOUNT_WINDOW && (
              <>
                <FeedSlideMedia
                  media={media}
                  type={type}
                  isActive={index === activeIndex}
                  shouldPreload={distance <= VIDEO_PRELOAD_WINDOW}
                  shouldShowPoster={distance <= POSTER_PRELOAD_WINDOW}
                  index={index}
                  isMuted={isMuted}
                  onAudioDetected={onAudioDetected}
                />
                <div className={classes.slideActionBar}>
                  <FeedActionBar
                    label="Try Style"
                    onUpload={onUploadTap}
                    onEdit={onEditTap}
                    canEdit={canEditPresetInputs(item)}
                    isLoading={isUploadLoading}
                    slug={slug}
                    shareTarget={shareTargets[index]}
                    flowId={flowId}
                    shouldLoadCredits={
                      shouldLoadCredits && distance <= CREDITS_QUOTE_WINDOW
                    }
                  />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default memo(FeedSlideList);
